import {
  apiv2_getCategories,
  apiv2_getFirms,
  apiv2_getFunds,
  apiv2_getIndustryInterval,
  apiv2_getMetaTerms,
} from "@/app/api2"
import { mainLogger as log } from "@/app/logger"
import { AppData } from "@/app/providers/app_data.class"
import {
  CategoriesList,
  FirmsList,
  FundsList,
  IS_SERVER,
  IndustryValues,
  Interval,
  MetaTerms,
  Term,
} from "@/app/types"

export function loadAll(data: AppData, filter: string) {
  if (IS_SERVER()) {
    log.info("Not loading API data on server")
    return Promise.resolve() // return a resolved promise
  }

  // TODO - use Workers threads to load in parallel
  const loaders = Promise.all([
    loadCategories().then((items) => {
      if (items) data.categoriesList = items
    }),
    loadFirms().then((items) => {
      if (items) data.firmsList = items
    }),
    loadFunds().then((items) => {
      if (items) data.fundsList = items
    }),
    loadIndustryMetrics(filter).then((items) => {
      if (items) data.industryMetrics = items
    }),
    loadTerms().then((items) => {
      if (items) data.metaTerms = items
    }),
  ])
    .catch((err) => {
      log.error("Error while loading AppData. err:", err)
    })
    .then((responses) => {
      log.info("Loading complete! num responses:", responses?.length)
    })

  return loaders
}

export async function loadTerms(): Promise<MetaTerms | null> {
  return apiv2_getMetaTerms().then((resp: any) => {
    // validate the response structure
    if (!("periodic" in resp) || !("ranking" in resp)) return null

    // for each interval, check all that all periodic/ranking terms have a valid key
    const checkIntervals = Interval.NAMES.every((interval) => {
      const periodicTerms: Term[] =
        interval in resp["periodic"] ? resp["periodic"][interval] : []
      const periodicTermsOk: boolean =
        Array.isArray(periodicTerms) && periodicTerms.every((term) => term?.key)
      const rankingTerms: Term[] =
        interval in resp["ranking"] ? resp["ranking"][interval] : []
      const rankingTermsOk: boolean =
        Array.isArray(rankingTerms) && rankingTerms.every((term) => term?.key)
      return periodicTermsOk && rankingTermsOk
    })

    if (checkIntervals) return resp
    log.error("Invalid response recieved while loading terms", resp)
    return null
  })
}

export async function loadFunds(): Promise<FundsList | null> {
  return apiv2_getFunds().then((resp: any) => {
    // validate the response structure, funds count, and that all funds have a valid fundId
    const count: number = "count" in resp ? resp.count : -1
    const fundsList: FundsList = "fundsList" in resp ? resp.fundsList : []
    if (
      Array.isArray(fundsList) &&
      fundsList.length == count &&
      fundsList.every((fund) => fund?.fundId)
    ) {
      // return funds list sorted in alphabetical (ignorecase) order
      return fundsList.toSorted((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    }
    log.error("Invalid response received while loading funds list", resp)
    return null
  })
}

export async function loadFirms(): Promise<FirmsList | null> {
  return apiv2_getFirms().then((resp: any) => {
    // validate the response structure, firms count, and that all funds have a valid firmId
    const count: number = "count" in resp ? resp.count : -1
    const firmsList: FirmsList = "firmsList" in resp ? resp.firmsList : []
    if (
      Array.isArray(firmsList) &&
      firmsList.length == count &&
      firmsList.every((firm) => firm?.firmId)
    ) {
      // return firms list sorted in alphabetical (ignorecase) order
      return firmsList.toSorted((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    }
    log.error("Invalid response received while loading firms list", resp)
    return null
  })
}

export async function loadCategories(): Promise<CategoriesList | null> {
  return apiv2_getCategories().then((resp: any) => {
    // validate the response structure, categories count, and that all categories have a valid categoryId
    const count: number = "count" in resp ? resp.count : -1
    const categoriesList: CategoriesList =
      "categoriesList" in resp ? resp.categoriesList : []
    if (
      Array.isArray(categoriesList) &&
      categoriesList.length == count &&
      categoriesList.every((category) => category?.categoryId)
    ) {
      return categoriesList
    }
    log.error("Invalid response received while loading categories list", resp)
    return null
  })
}

// to load all industry metric, iterate over all intervals and returns a single Promise that resolves to the new metrics
export async function loadIndustryMetrics(
  filter: string
): Promise<IndustryValues | null> {
  const newIndustryMetrics = new IndustryValues()
  log.info("Loading industry data for", Interval.NAMES)
  await Promise.all(
    Interval.NAMES.map((interval) =>
      apiv2_getIndustryInterval(interval, filter).then((v) => {
        log.debug("Loaded industry data for interval", interval, v)
        newIndustryMetrics.set(interval, v)
      })
    )
  )
  // if either of daily/monthly/quarterly/annual is null, then dont save this object
  return Promise.resolve<IndustryValues | null>(
    newIndustryMetrics.isSet() ? newIndustryMetrics : null
  )
}
